import { GetStaticPaths, GetStaticProps } from 'next';

import ApiService from '@/lib/api';
import CustomPage from '@/components/CustomPage';
import { CustomRouteWithPage } from '@/types/pages';
import { Namespaces } from '@/types/pages';
import React from 'react';
import { getDraftOrPageContent } from '@/utils/helpers';

export const getStaticPaths: GetStaticPaths = async () => {
  const apiService = new ApiService();
  const postList = await apiService.getCustomPageRoutes(Namespaces.APPROACH);

  return {
    paths: postList
      .filter((route) => route.slug?.current) // Ensure slug is valid
      .map((route) => ({
        params: { slug: route.slug.current.split('/') },
      })),
    // 'blocking' so an unknown slug gets a real 404 on its first request (FOX2-202).
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params, preview = null }) => {
  const apiService = new ApiService();
  const slug = params?.slug ? (Array.isArray(params.slug) ? params.slug.join('/') : params.slug) : '';

  const [data, menuData, { footer }] = await Promise.all([
    apiService.getCustomPageContent(slug, preview, Namespaces.APPROACH),
    apiService.getMenuData(),
    apiService.getMiscData(),
  ]);
  const pageData = getDraftOrPageContent(data);
  if (!pageData?.content) {
    // Revalidate the 404 too: on-demand revalidation doesn't reach namespaced
    // paths, so a 404 cached before an editor files a route under approach/
    // would otherwise stick until the next deploy.
    return { notFound: true, revalidate: 60 };
  }
  return {
    props: {
      content: pageData.content,
      isDraft: pageData.isDraft ?? false,
      menuData,
      footer,
      slug: slug || null,
    },
    revalidate: 3600,
  };
};

interface ApproachPageProps {
  content: CustomRouteWithPage | null;
  menuData?: unknown;
  footer?: unknown;
  slug?: string;
  isDraft: boolean | null;
}

const ApproachPage: React.FC<ApproachPageProps> = (props) => {
  const { content, menuData, footer, slug, isDraft } = props;
  const ogUrl = `https://foxbox.com/approach/${slug}`;
  return (
    <CustomPage
      content={content}
      menuData={menuData}
      footer={footer}
      slug={slug}
      isDraft={isDraft}
      ogUrl={ogUrl}
      sectionLink={{ href: '/approach', label: '← Our approach' }}
    />
  );
};

export default ApproachPage;
